import { Button, Col, Form, Row } from 'react-bootstrap';
import Flex from 'components/common/Flex';
import { useMemo, useState } from 'react';
import { filesBase64 } from 'utils/files/filesToBase64';
import DropFiles from '../content/DropFiles';
import { useSelector } from 'react-redux';
import { useGetContentMutation } from 'utils/rest/content/getContent';
import { useUploadContentMutation } from 'utils/rest/content/uploadContent';
import { faQuestion } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  testFilesFormat,
  testFloorPlanFormat,
  testImagesFormat,
  testShopFitFormat
} from 'utils/files/testFilesFormat';

const PortfolioForm = ({
  close,
  prev,
  form,
  fp,
  imgs,
  rdrs,
  sf,
  ph,
  rendersUri,
  setRendersUri,
  uri,
  setImagesUri
}) => {
  const [hover, setHover] = useState();
  const [images, setImages] = useState(imgs ?? []);
  const [renders, setRenders] = useState(rdrs ?? []);
  const [floorPlan, setFloorPlan] = useState(fp ?? []);
  const [shopFit, setShopFit] = useState(sf ?? []);
  const [projects, setProjects] = useState(ph ?? []);
  const [dragging, setDragging] = useState(false);
  const [formData, setFormData] = useState(
    form ?? {
      category01ISbb_q4sg_content_categories2ID: '',
      category02ISbb_q4sg_content_categories2ID: '',
      content01ISbb_q4sg_contentID: ''
    }
  );

  const { mutate: uploadContent, isPending: contentLoading } =
    useUploadContentMutation();
  const { mutate: getContent } = useGetContentMutation();

  const { new_tree } = useSelector(state => state.appsetting);

  const { countryList, storeList } = useMemo(() => {
    const countryList = new_tree?.sub_categories
      .find(el => el.name.toLowerCase().includes('shops'))
      .sub_categories.find(el =>
        el.name.toLowerCase().includes('portfolio')
      ).sub_categories;

    const storeList = countryList.find(
      el => el._id === formData.category01ISbb_q4sg_content_categories2ID
    )?.content;

    return { countryList, storeList };
  }, [new_tree, formData]);

  const handleBack = () => {
    prev();
    close();
  };

  const handleChange = e => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async e => {
    e.preventDefault();

    if (formData.category) {
      await setFormData({
        ...formData,
        ...(formData.category = null),
        ...(formData.selected_pathISplaintextbox = catPath)
      });
    }

    const dataFlr = await filesBase64(formData, floorPlan, 'flr');
    const dataImg = await filesBase64(dataFlr, images, 'img');
    const dataRrd = await filesBase64(dataImg, renders, 'rdr');
    const dataShops = await filesBase64(dataRrd, shopFit, 'shopfit');
    const dataProj = await filesBase64(dataShops, projects, 'projects');

    await uploadContent(dataProj, {
      onSuccess: () => {
        close();
        getContent(user?._id);
      }
    });
  };

  const handleDragOver = e => {
    e.preventDefault();
    setDragging(true);
  };

  const handleDragLeave = () => {
    setDragging(false);
  };

  const handleDrop = async (e, type) => {
    e.preventDefault();
    setDragging(false);
    const droppedFiles = Array.from(e.dataTransfer.files);

    if (type === 'flr') {
      const validated = testFloorPlanFormat(floorPlan, droppedFiles);
      setFloorPlan(validated);
    }
    if (type === 'img') {
      const validated = await testImagesFormat(images, droppedFiles);
      setImages(validated);
    }
    if (type === 'rdr') {
      const validated = await testImagesFormat(renders, droppedFiles);
      setRenders(validated);
    }
    if (type === 'shopfit') {
      const validated = await testShopFitFormat(shopFit, droppedFiles);
      setShopFit(validated);
    }
    if (type === 'projects') {
      const validated = await testFilesFormat(shopFit, droppedFiles, 'short');
      setProjects(validated);
    }
  };

  return (
    <Form onSubmit={handleSubmit} className="px-6 pt-4">
      <div className="d-grid grid-cols-2 row">
        <fieldset className={'form-legend-item-wrapper mb-4'}>
          <legend className="item-title fs-1 fw-semi-bold">
            Select Country
          </legend>
          <Form.Control
            as="select"
            className="form-select"
            value={formData?.category01ISbb_q4sg_content_categories2ID}
            name="category01ISbb_q4sg_content_categories2ID"
            onChange={handleChange}
            required
          >
            <option value="" disabled>
              Select
            </option>
            {countryList?.map((country, idx) => (
              <option value={country._id} key={idx} onChange={handleChange}>
                {country.name}
              </option>
            ))}
          </Form.Control>
        </fieldset>

        <fieldset className={'form-legend-item-wrapper mb-4'}>
          <legend className="item-title fs-1 fw-semi-bold">Select Store</legend>
          <Form.Control
            disabled={formData?.category01ISbb_q4sg_content_categoriesID === ''}
            as="select"
            className="form-select"
            value={formData.content01ISbb_q4sg_contentID}
            name="content01ISbb_q4sg_contentID"
            onChange={handleChange}
            required
          >
            <option value="" disabled>
              Select
            </option>
            {storeList && storeList.length > 0 ? (
              storeList?.map((content, idx) => (
                <option value={content._id} key={idx} onChange={handleChange}>
                  {content.name}
                </option>
              ))
            ) : (
              <option>No content list for this country</option>
            )}
          </Form.Control>
        </fieldset>
      </div>
      <Row>
        <fieldset
          className="form-legend-item-wrapper mb-4 position-relative"
          style={{ zIndex: 50 }}
        >
          <legend className="item-title fs-1 d-flex align-items-center">
            <span className="cursor-pointer fw-semi-bold">Floorplan</span>
            <div
              className="mx-2 info-block position-relative"
              onMouseEnter={() => setHover('floor')}
              onMouseLeave={() => setHover('')}
            >
              <FontAwesomeIcon icon={faQuestion} size="xs" />
              {hover === 'floor' && (
                <div className="position-absolute position-info">
                  <ul className="m-0">
                    <li>
                      Only <strong>.pdf</strong> file allowed.
                    </li>
                    <li>
                      You can upload only <strong>1 file</strong> maximum.
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </legend>
          <label
            style={{
              minHeight: '120px',
              maxHeight: '120px',
              width: '100%'
            }}
            onDragOver={e => handleDragOver(e, 'flr')}
            onDragLeave={() => handleDragLeave('flr')}
            onDrop={e => handleDrop(e, 'flr')}
          >
            <DropFiles
              dragging={dragging}
              items={floorPlan}
              setFiles={setFloorPlan}
              form={formData}
              setForm={setFormData}
              type={'floor'}
            />
          </label>
        </fieldset>
      </Row>

      <Row>
        <fieldset
          className="form-legend-item-wrapper mb-4 position-relative"
          style={{ zIndex: 50 }}
        >
          <legend className="item-title fs-1 d-flex align-items-center">
            <span className="cursor-pointer fw-semi-bold">Images</span>
            <div
              className="mx-2 info-block position-relative"
              onMouseEnter={() => setHover('images')}
              onMouseLeave={() => setHover('')}
            >
              <FontAwesomeIcon icon={faQuestion} size="xs" />
              {hover === 'images' && (
                <div className="position-absolute position-info">
                  <ul className="m-0">
                    <li>
                      Only <strong>.zip</strong> or <strong>image</strong> files
                      are allowed.
                    </li>
                    <li>
                      The <strong>.zip</strong> archive must not contain any
                      folders
                    </li>
                    <li>
                      Accepted image formats: <strong>.png</strong> and{' '}
                      <strong>.jpg</strong>.
                    </li>
                    <li>
                      You can upload up to <strong>9 images</strong> maximum.
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </legend>
          <label
            style={{
              minHeight: '120px',
              maxHeight: '120px',
              width: '100%'
            }}
            onDragOver={e => handleDragOver(e, 'img')}
            onDragLeave={() => handleDragLeave('img')}
            onDrop={e => handleDrop(e, 'img')}
          >
            <DropFiles
              dragging={dragging}
              items={images}
              setFiles={setImages}
              form={formData}
              setForm={setFormData}
              type={'images'}
              uri={uri}
              setImagesUri={setImagesUri}
            />
          </label>
        </fieldset>
      </Row>
      <Row>
        <fieldset
          className="form-legend-item-wrapper mb-4 position-relative"
          style={{ zIndex: 50 }}
        >
          <legend className="item-title fs-1 d-flex align-items-center">
            <span className="cursor-pointer fw-semi-bold">Renders</span>
            <div
              className="mx-2 info-block position-relative"
              onMouseEnter={() => setHover('render')}
              onMouseLeave={() => setHover('')}
            >
              <FontAwesomeIcon icon={faQuestion} size="xs" />
              {hover === 'render' && (
                <div className="position-absolute position-info">
                  <ul className="m-0">
                    <li>
                      Only <strong>.zip</strong> or <strong>image</strong> files
                      are allowed.
                    </li>
                    <li>
                      The <strong>.zip</strong> archive must not contain any
                      folders
                    </li>
                    <li>
                      Accepted image formats: <strong>.png</strong> and{' '}
                      <strong>.jpg</strong>.
                    </li>
                    <li>
                      You can upload up to <strong>9 images</strong> maximum.
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </legend>
          <label
            style={{
              minHeight: '120px',
              maxHeight: '120px',
              width: '100%'
            }}
            onDragOver={e => handleDragOver(e, 'rdr')}
            onDragLeave={() => handleDragLeave('rdr')}
            onDrop={e => handleDrop(e, 'rdr')}
          >
            <DropFiles
              dragging={dragging}
              items={renders}
              setFiles={setRenders}
              form={formData}
              setForm={setFormData}
              type={'renders'}
              uri={rendersUri}
              setImagesUri={setRendersUri}
            />
          </label>
        </fieldset>
      </Row>
      <Row>
        <fieldset
          className="form-legend-item-wrapper mb-4 position-relative"
          style={{ zIndex: 50 }}
        >
          <legend className="item-title fs-1 d-flex align-items-center">
            <span className="cursor-pointer fw-semi-bold">
              Shop Fit Information
            </span>
            <div
              className="mx-2 info-block position-relative"
              onMouseEnter={() => setHover('shopfit')}
              onMouseLeave={() => setHover('')}
            >
              <FontAwesomeIcon icon={faQuestion} size="xs" />
              {hover === 'shopfit' && (
                <div className="position-absolute position-info">
                  <ul className="m-0">
                    <li>
                      Only <strong>.zip</strong> or <strong>docs</strong> files
                      are allowed.
                    </li>
                    <li>
                      The <strong>.zip</strong> archive must include the
                      original folder and subfolder structure (for example:{' '}
                      <em>documents/RD documents/...</em>).
                    </li>
                    <li>
                      Accepted docs formats: <strong>.pdf, .docx</strong> and{' '}
                      <strong>.xlsx</strong>.
                    </li>
                    <li>
                      You can upload up to <strong>3 docs</strong> maximum.
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </legend>
          <label
            style={{
              minHeight: '120px',
              maxHeight: '120px',
              width: '100%'
            }}
            onDragOver={e => handleDragOver(e, 'shopfit')}
            onDragLeave={() => handleDragLeave('shopfit')}
            onDrop={e => handleDrop(e, 'shopfit')}
          >
            <DropFiles
              dragging={dragging}
              items={shopFit}
              setFiles={setShopFit}
              form={formData}
              setForm={setFormData}
              type={'shopfit'}
            />
          </label>
        </fieldset>
      </Row>
      <Row>
        <fieldset
          className="form-legend-item-wrapper mb-4 position-relative"
          style={{ zIndex: 50 }}
        >
          <legend className="item-title fs-1 d-flex align-items-center">
            <span className="cursor-pointer fw-semi-bold">
              Projects History
            </span>
            <div
              className="mx-2 info-block position-relative"
              onMouseEnter={() => setHover('projects')}
              onMouseLeave={() => setHover('')}
            >
              <FontAwesomeIcon icon={faQuestion} size="xs" />
              {hover === 'projects' && (
                <div className="position-absolute position-info">
                  <ul className="m-0">
                    <li>
                      Only <strong>.zip</strong> or <strong>docs</strong> files
                      are allowed.
                    </li>
                    <li>
                      The <strong>.zip</strong> archive must not contain any
                      folders
                    </li>
                    <li>
                      Accepted docs formats: <strong>.pdf, .docx</strong> and{' '}
                      <strong>.xlsx</strong>.
                    </li>
                    <li>
                      You can upload up to <strong>3 docs</strong> maximum.
                    </li>
                  </ul>
                </div>
              )}
            </div>
          </legend>
          <label
            style={{
              minHeight: '120px',
              maxHeight: '120px',
              width: '100%'
            }}
            onDragOver={e => handleDragOver(e, 'projects')}
            onDragLeave={() => handleDragLeave('projects')}
            onDrop={e => handleDrop(e, 'projects')}
          >
            <DropFiles
              dragging={dragging}
              items={projects}
              setFiles={setProjects}
              form={formData}
              setForm={setFormData}
              type={'projects'}
            />
          </label>
        </fieldset>
      </Row>

      <Flex className="gap-5">
        <Button variant="primary" type="submit" disabled={contentLoading}>
          Submit For Approval
        </Button>
        {prev && (
          <Button
            variant="primary"
            type="button"
            onClick={handleBack}
            disabled={contentLoading}
          >
            Back
          </Button>
        )}
      </Flex>
    </Form>
  );
};

export default PortfolioForm;
